import { apiRequest, ApiResponse } from '../request'

export type PayoutRecipientKind =
    | 'PARTNER'
    | 'PROFESSIONAL'
    | 'HOSPITAL'
    | 'SUPPLIER'

/**
 * Filtros do agrupamento. Subconjunto do que a aba Lançamentos oferece — o que fica
 * de fora é fixo por regra do backend: só `PAYABLE`, só categorias de repasse, só o
 * que está em aberto (agrupar repasse já pago pagaria o destinatário duas vezes).
 */
export interface PayoutBatchFilters {
    startDate?: string
    endDate?: string
    dateBasis?: 'created' | 'due'
    scope?: 'company' | 'organization'
    /**
     * Filial escolhida no seletor do topo do relatório. Sem ela, o agrupamento
     * consultava a unidade ativa do usuário enquanto a listagem mostrava a filial
     * selecionada — a tela listava repasses e o modal dizia que não havia nenhum.
     */
    companyId?: number
    partnerId?: number
    professionalId?: number
    hospitalId?: number
    supplierId?: number
}

export interface PayoutBatchItem {
    billingId: number
    amount: number
    dueDate: string | null
    createdAt: string
    description: string | null
    category: string | null
    serviceDate: string | null
    /** Paciente do atendimento — a chave do cruzamento com o relatório do parceiro. */
    clientName: string | null
}

/**
 * Um destinatário no preview: quem, quantos e quanto — **sem a composição**.
 *
 * A lista de lançamentos vem por `previewPayoutBatchItems`, paginada e só de quem o
 * operador abrir. Um filtro amplo pode ter dezenas de milhares de repasses; trazê-los
 * todos para montar a tela era o que travava a conferência.
 */
export interface PayoutBatchGroup {
    kind: PayoutRecipientKind
    kindLabel: string
    entityId: number
    name: string
    document: string | null
    count: number
    totalAmount: number
    /**
     * Ciclo de repasse do cadastro do destinatário. `null` quando ele está em `OPEN`
     * (o padrão): o agrupamento dele só acontece à mão, até alguém definir a data.
     */
    schedule: {
        type: string
        fixedDay: number | null
        daysAfter: number | null
    } | null
}

/** Uma página da composição de um destinatário. */
export interface PayoutBatchItemsPage {
    items: PayoutBatchItem[]
    total: number
    totalAmount: number
    page: number
    pageSize: number
    pageCount: number
}

/**
 * O que o filtro pegou e o agrupamento não leva, por motivo.
 *
 * A aba Lançamentos lista toda conta a pagar do período; o agrupamento só aceita
 * repasse em aberto, ainda sem lote e com destinatário. Sem estes números, a
 * diferença entre as duas listas chegava ao operador como um "nenhum repasse em
 * aberto" sem explicação, com lançamentos visíveis na tela atrás do modal.
 */
export interface PayoutBatchSkipped {
    total: number
    notPayoutCategory: number
    alreadyBatched: number
    notOpen: number
    withoutRecipient: number
}

export interface PayoutBatchPreview {
    groups: PayoutBatchGroup[]
    totalAmount: number
    totalCount: number
    skipped: PayoutBatchSkipped
}

export interface PayoutBatchCreated {
    batchId: number
    recipientName: string
    amount: number
    itemCount: number
    pdfUrl: string | null
}

export interface PayoutBatchResult {
    created: PayoutBatchCreated[]
    skipped: Array<{ recipientName: string; reason: string }>
}

function buildParams(
    filters: PayoutBatchFilters | Record<string, unknown>
): Record<string, string> {
    const params: Record<string, string> = {}
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params[key] = String(value)
        }
    })
    return params
}

export async function previewPayoutBatch(
    filters: PayoutBatchFilters
): Promise<ApiResponse<PayoutBatchPreview>> {
    return apiRequest<PayoutBatchPreview>('financial/payout-batches/preview', {
        params: buildParams(filters),
    })
}

/**
 * Uma página dos repasses em aberto de um destinatário — o que o operador confere e
 * marca (ou desmarca) antes de gerar. A busca aceita o número do lançamento, parte da
 * descrição ou o **nome do paciente**.
 */
export async function previewPayoutBatchItems(
    filters: PayoutBatchFilters,
    recipient: { kind: PayoutRecipientKind; entityId: number },
    options: { page?: number; pageSize?: number; search?: string } = {}
): Promise<ApiResponse<PayoutBatchItemsPage>> {
    return apiRequest<PayoutBatchItemsPage>(
        'financial/payout-batches/preview/items',
        {
            params: buildParams({
                ...filters,
                ...recipient,
                ...options,
            } as Record<string, unknown>),
        }
    )
}

/** Um repasse individual dentro de uma solicitação agrupada. */
export interface PayoutBatchDetailItem {
    billingId: number
    amount: number
    status: string
    dueDate: string | null
    description: string | null
    serviceDate: string | null
    /** Paciente do atendimento — a chave do cruzamento com o relatório do parceiro. */
    clientName: string | null
}

export interface PayoutBatchItems {
    items: PayoutBatchDetailItem[]
    total: number
    totalAmount: number
    page: number
    pageSize: number
    pageCount: number
}

/**
 * O que compõe a solicitação agrupada — usado pelo "Ver grupo" nas aprovações e pela
 * seção de composição na edição. Paginado: um lote pode somar milhares de atendimentos.
 */
export async function getPayoutBatchItems(
    batchId: number,
    options: { page?: number; pageSize?: number; search?: string } = {}
): Promise<ApiResponse<PayoutBatchItems>> {
    return apiRequest<PayoutBatchItems>(
        `financial/payout-batches/${batchId}/items`,
        { params: buildParams(options as Record<string, unknown>) }
    )
}

/**
 * O recorte que o operador fez na composição, nas duas direções possíveis.
 *
 * - `exclude`: entra tudo do filtro **menos** os ids (o padrão);
 * - `include`: entra **apenas** os ids (o botão "Inverter", para quando só um punhado
 *   de uma lista longa deve ser pago agora).
 *
 * Em qualquer um dos dois, nada é marcado no lançamento: o que ficou de fora continua
 * em aberto, volta no próximo preview e entra normalmente no agrupamento automático.
 */
export interface PayoutBatchSelection {
    mode: 'exclude' | 'include'
    ids: number[]
}

export async function generatePayoutBatch(
    filters: PayoutBatchFilters,
    recipients?: Array<{ kind: PayoutRecipientKind; entityId: number }>,
    selection?: PayoutBatchSelection
): Promise<ApiResponse<PayoutBatchResult>> {
    const ids = selection?.ids ?? []
    return apiRequest<PayoutBatchResult>(
        'financial/payout-batches',
        {
            data: {
                filters,
                ...(recipients?.length ? { recipients } : {}),
                ...(ids.length
                    ? selection!.mode === 'include'
                        ? { includeIds: ids }
                        : { excludeIds: ids }
                    : {}),
            },
        },
        'post'
    )
}

/**
 * Tira um lançamento de uma solicitação já criada.
 *
 * Recusado depois de a solicitação ser aprovada (o valor não pode mais mudar) e
 * enquanto houver formas de pagamento divididas — os valores delas somam o total
 * atual.
 */
export async function removePayoutBatchItem(
    batchId: number,
    billingId: number
): Promise<
    ApiResponse<{
        removedId: number
        remainingCount: number
        remainingAmount: number
    }>
> {
    return apiRequest(
        `financial/payout-batches/${batchId}/items/${billingId}`,
        {},
        'delete'
    )
}

/**
 * Envia para a fila de aprovação a solicitação agrupada que o financeiro configurou.
 *
 * É neste momento que o demonstrativo em PDF é gerado — com vencimento e formas de
 * pagamento já decididos. Antes disso a solicitação existe, mas fora da fila: o
 * aprovador só deve receber o que está pronto para um clique.
 */
export async function submitPayoutBatch(
    batchId: number
): Promise<ApiResponse<{ id: number; splitCount: number }>> {
    return apiRequest(
        `financial/payout-batches/${batchId}/submit`,
        {},
        'post'
    )
}

/** Uma solicitação agrupada que existe mas nunca foi enviada para aprovação. */
export interface PayoutBatchAwaitingSubmission {
    id: number
    recipientName: string
    amount: number
    itemCount: number
    status: string
    dueDate: string | null
    createdAt: string
    companyName: string | null
}

/**
 * Solicitações agrupadas criadas e esquecidas antes do "Enviar para aprovação".
 *
 * Elas não aparecem em Aprovações (nascem fora da fila) nem se distinguem de uma
 * conta a pagar comum na listagem — e, enquanto isso, prendem os repasses que somam,
 * que somem do agrupamento. A fila de aprovações avisa a partir daqui.
 */
export async function getPayoutBatchesAwaitingSubmission(): Promise<
    ApiResponse<{
        items: PayoutBatchAwaitingSubmission[]
        totalAmount: number
    }>
> {
    return apiRequest('financial/payout-batches/awaiting-submission', {})
}
